import { create } from 'zustand'
import { apiClient } from '../lib/api'
import type {
  AnalyticsOverview,
  CostDataPoint,
  UsageDataPoint,
  FeatureData,
  SessionData,
} from '../lib/types'

interface AnalyticsState {
  overview: AnalyticsOverview | null
  costs: CostDataPoint[]
  usage: UsageDataPoint[]
  features: FeatureData[]
  sessions: SessionData | null
  loading: boolean
  error: string | null

  fetchOverview: () => Promise<void>
  fetchCosts: (period: string) => Promise<void>
  fetchUsage: (period: string) => Promise<void>
  fetchFeatures: () => Promise<void>
  fetchSessions: () => Promise<void>
}

export const useAnalyticsStore = create<AnalyticsState>((set) => ({
  overview: null,
  costs: [],
  usage: [],
  features: [],
  sessions: null,
  loading: false,
  error: null,

  fetchOverview: async () => {
    set({ loading: true, error: null })
    try {
      const data = await apiClient.get<AnalyticsOverview>('analytics/overview/')
      set({ overview: data })
    } catch {
      set({ error: 'Failed to load dashboard.' })
    } finally {
      set({ loading: false })
    }
  },

  fetchCosts: async (period: string) => {
    try {
      const data = await apiClient.get<CostDataPoint[]>(`analytics/costs/?period=${period}`)
      set({ costs: data })
    } catch {
      set({ error: 'Failed to load cost data.' })
    }
  },

  fetchUsage: async (period: string) => {
    try {
      const data = await apiClient.get<UsageDataPoint[]>(`analytics/usage/?period=${period}`)
      set({ usage: data })
    } catch {
      set({ error: 'Failed to load usage data.' })
    }
  },

  fetchFeatures: async () => {
    try {
      const data = await apiClient.get<FeatureData[]>('analytics/features/')
      set({ features: data })
    } catch {
      set({ error: 'Failed to load feature data.' })
    }
  },

  fetchSessions: async () => {
    try {
      const data = await apiClient.get<SessionData>('analytics/sessions/')
      set({ sessions: data })
    } catch {
      set({ error: 'Failed to load session data.' })
    }
  },
}))
